import React, {useEffect, useRef, useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Pagination} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import {personsIcons, sliderShild, arrowDown} from '@/assets/images/index.js';

const REVIEWS_APP_CLASS = 'elfsight-app-08d84d44-9062-4e7c-b2d3-2c8ca9204921';

// Module-level persistent widget nodes, keyed by slot — same pattern as
// ElfsightChat: the Elfsight platform initializes a div a single time, so
// letting React destroy it on unmount forces a full widget reload (skeleton
// and all) every time the user navigates back. Each slot's div is created
// ONCE, adopted into the page on mount and parked in a hidden holder on
// unmount, so the widget only ever loads on the first visit after a reload.
const reviewSlots = new Map();
let parkingLot = null;

function acquireSlot(slot) {
    if (!reviewSlots.has(slot)) {
        const el = document.createElement('div');
        el.className = REVIEWS_APP_CLASS;
        el.setAttribute('data-elfsight-app-lazy', '');
        reviewSlots.set(slot, el);
    }
    return reviewSlots.get(slot);
}

function park(el) {
    if (!parkingLot) {
        parkingLot = document.createElement('div');
        parkingLot.style.display = 'none';
        document.body.appendChild(parkingLot);
    }
    parkingLot.appendChild(el);
}

function ReviewsEmbed({slot}) {
    const hostRef = useRef(null);

    useEffect(() => {
        const el = acquireSlot(slot);
        hostRef.current.appendChild(el);
        // platform.js is loaded globally in index.html. If it already ran its
        // initial scan before this div existed, nudge it to pick the div up
        // from the buffer; if it hasn't loaded yet, it scans the DOM itself.
        if (!el.childElementCount) {
            window.eapps?.Platform?.initWidgetsFromBuffer?.();
        }
        return () => park(el);
    }, [slot]);

    return <div ref={hostRef}></div>;
}

export function HopnReviewsWidget({layout = 'carousel'}) {
    // `collapsible` layout only — starts closed, showing just the rating summary.
    const [open, setOpen] = useState(false);

    // Collapsible: the rating summary (G logo + score + stars + count) is the header
    // and the only thing visible when closed; the chevron opens the text reviews
    // FULL WIDTH underneath (rather than the 60% column the split layout gives them).
    //
    // The body stays MOUNTED and is collapsed with max-height, not unmounted — same
    // reason ReviewsEmbed parks its node instead of letting React drop it: the
    // Elfsight app initialises itself into the div, so tearing it down would make it
    // reload from scratch on every toggle.
    if (layout === 'collapsible') {
        return (
            <div className={`hopn-reviews-widget hopn-reviews-widget--collapsible ${open ? 'is-open' : ''}`}>
                {/* A div, not a <button>: the Elfsight summary inside is itself
                    interactive, and nested buttons are invalid HTML. */}
                <div className="hopn-rev-toggle" role="button" tabIndex={0}
                     aria-expanded={open}
                     onClick={() => setOpen((o) => !o)}
                     onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); } }}>
                    <div className="hopn-rev-summary">
                        <ReviewsEmbed slot="collapsible-summary"/>
                    </div>
                    <img className="hopn-rev-chev" src={arrowDown}
                         alt={open ? 'Hide reviews' : 'Show reviews'}/>
                </div>

                <div className="hopn-rev-body">
                    <div className="hopn-rev-slider">
                        <ReviewsEmbed slot="collapsible-slider"/>
                    </div>
                </div>
            </div>
        );
    }

    // Summary-only: just the Google rating card (G logo + score + stars + count),
    // no text-review slider. Same Elfsight app, cropped by .hopn-rev-summary.
    if (layout === 'summary') {
        return (
            <div className="hopn-reviews-widget hopn-reviews-widget--summary">
                <div className="hopn-rev-summary">
                    <ReviewsEmbed slot="summary-only"/>
                </div>
            </div>
        );
    }

    // Split layout: 40% rating-summary review + 60% text-reviews slider, both
    // fed by the same Elfsight app, vertically centered. Used on selectedCarDetails.
    if (layout === 'split') {
        return (
            <div className="hopn-reviews-widget hopn-reviews-widget--split">
                <div className="hopn-reviews-split">
                    <div className="hopn-reviews-split__summary">
                        <div className="hopn-rev-summary">
                            <ReviewsEmbed slot="split-summary"/>
                        </div>
                    </div>
                    <div className="hopn-reviews-split__slider">
                        <div className="hopn-rev-slider">
                            <ReviewsEmbed slot="split-slider"/>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="hopn-reviews-widget">
            <div className="lt-carousel lt-carousel-trust">
                <div className="lt-carousel__viewport">
                    <Swiper
                        modules={[Pagination]}
                        spaceBetween={0}
                        pagination={{clickable: true}}
                        breakpoints={{
                            0: {slidesPerView: 1.2, spaceBetween: 8},
                            991: {slidesPerView: 4}
                        }}
                        className="lt-carousel__container"
                    >
                        <SwiperSlide className="lt-carousel__slide">
                            <div className="trust-card trust-card--shortcode">
                                <div className="only-review-widget">
                                    <ReviewsEmbed slot="carousel-rating"/>
                                </div>
                            </div>
                        </SwiperSlide>

                        <SwiperSlide className="lt-carousel__slide">
                            <div className="trust-card trust-card--shortcode">
                                <div className="only-review-text">
                                    <ReviewsEmbed slot="carousel-text"/>
                                </div>
                            </div>
                        </SwiperSlide>

                        <SwiperSlide className="lt-carousel__slide">
                            <div className="trust-card">
                                <div className="trust-card__row trust-card__row--has-icon trust-card__row--icon-left">
                                    <div className="trust-card__icon">
                                        <img src={personsIcons} alt=""/>
                                    </div>
                                    <div className="trust-card__content"><strong>10,000+</strong>Rides completed</div>
                                </div>
                                <div className="trust-card__description">Over 18 Years in Business</div>
                            </div>
                        </SwiperSlide>

                        <SwiperSlide className="lt-carousel__slide">
                            <div className="trust-card">
                                <div className="trust-card__row trust-card__row--has-icon trust-card__row--icon-left">
                                    <div className="trust-card__icon">
                                        <img src={sliderShild} alt=""/>
                                    </div>
                                    <div className="trust-card__content"><strong>0 Missed pickups</strong></div>
                                </div>
                                <div className="trust-card__description">Last 12 months</div>
                            </div>
                        </SwiperSlide>
                    </Swiper>
                </div>
            </div>
        </div>
    );
}

export default HopnReviewsWidget;
